import { Box, Button, Flex, HStack, IconButton, Image, Text, Tooltip, useDisclosure } from "@chakra-ui/react";
import { Virtuoso } from "react-virtuoso";
import { useEffect, useRef, useState } from "react";
import { RiDeleteBin4Line, RiCheckboxCircleLine, RiLogoutBoxRLine, RiSendPlaneLine } from "@remixicon/react";
import { Content } from "@/types/content";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { contentRemove, selectContentsAll } from "@/reducer/contentSlice";
import { selectCurrentImage, setCurrentImage } from "@/reducer/designslice";
import { updateFiles } from "@/reducer/modeslice";
import Client4 from "@/services/api/client4";
import Publish from "../publish";
import TransitionDialog from "../alertDialog";
import HistoryModal from "./historyModal/historyModal";

const GenImageHistory = () => {
  const dispatch = useAppDispatch();
  const contents = useAppSelector(selectContentsAll);
  const currentImage = useAppSelector(selectCurrentImage);

  const [imgIndex, setImgIndex] = useState(-1);
  const [currentContentId, setCurrentContentId] = useState("");
  const [isShowHistoryList, setIsShowHistoryList] = useState(true);
  const publishControl = useDisclosure();
  const dialogControl = useDisclosure();
  const historyControl = useDisclosure();
  const virtuosoRef = useRef<any>(null);

  const onStructure = (content: Content) => {
    dispatch(updateFiles([content.image_url]));
  };

  const onPublish = (content: Content) => {
    setCurrentContentId(content.id);
    publishControl.onOpen();
  };

  const onDelete = (content: Content) => {
    setCurrentContentId(content.id);
    dialogControl.onOpen();
  };

  const handleOnDelete = () => {
    dispatch(contentRemove(currentContentId));
    Client4.deleteContent(currentContentId);

    if (currentImage.id === currentContentId) {
      const nextImage = contents.find((item) => item.id !== currentContentId) ?? ({} as Content);
      dispatch(setCurrentImage(nextImage));
    }

    dialogControl.onClose();
  };

  const handleClickImage = (item: Content) => {
    dispatch(setCurrentImage(item));
  };

  useEffect(() => {
    if (virtuosoRef.current?.scrollTo) {
      virtuosoRef.current.scrollTo({ top: 0 });
    }
  }, [contents]);

  return (
    <Box w="170px" h="100%" bgColor="#171920">
      <Flex h="100%" flexDirection="column">
        <Flex align="center" justify="space-between" px="8px" py="8px" gap="8px" borderBottom="1px solid rgba(255, 255, 255, 0.06)">
          <HStack spacing="6px" minW={0}>
            <Button variant="unstyled" color="white" fontSize="12px" fontWeight={500} px="4px" minW="auto" h="28px" onClick={() => setIsShowHistoryList((prevState) => !prevState)}>
              查看生图历史
            </Button>
          </HStack>

          <Button variant="unstyled" color="#8E94A9" fontSize="12px" fontWeight={500} minW="auto" h="28px" onClick={historyControl.onOpen}>
            全部
          </Button>
        </Flex>

        {isShowHistoryList && (
          <Box h="0" flex={1}>
            <Box h="100%">
              <Virtuoso
                style={{ height: "100%" }}
                ref={virtuosoRef}
                data={contents}
                followOutput
                itemContent={(index, item) => (
                  <Box position="relative" key={index} display="inline-block" ml="10px" boxSizing="border-box" border={currentImage.id === item.id ? "1px solid #3bd926" : "none"} onMouseEnter={() => setImgIndex(index)} onMouseLeave={() => setImgIndex(-1)} onClick={() => handleClickImage(item)}>
                    <Text
                      sx={{
                        position: "absolute",
                        top: "5px",
                        left: "5px",
                        bgColor: "#000000",
                        opacity: 0.5,
                        fontSize: "12px",
                        color: "white",
                        textAlign: "center",
                        w: "24px",
                        h: "16px",
                        lineHeight: "16px",
                      }}
                    >
                      {index + 1}
                    </Text>

                    <Text
                      sx={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        bgColor: "#000000",
                        opacity: 0.5,
                        fontSize: "12px",
                        color: "white",
                        textAlign: "center",
                        h: "16px",
                        lineHeight: "16px",
                        pl: "2px",
                        pr: "2px",
                      }}
                    >
                      {item.type === "TextToImage" ? "生图" : item.type === "Outpaint" ? "扩图" : item.type === "SmrToImage" ? "毛坯房出图" : item.type === "Upload" ? "上传" : ""}
                    </Text>

                    {currentImage.id === item.id && (
                      <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)">
                        <RiCheckboxCircleLine color="#3bd926" size="36px" />
                      </Box>
                    )}

                    <HStack
                      sx={{
                        background: "linear-gradient(0deg, rgba(39, 49, 77, 0.7) 0%, rgba(39, 49, 77, 0) 100%)",
                        position: "absolute",
                        h: "24px",
                        w: "100%",
                        bottom: 0,
                      }}
                      spacing="5px"
                      opacity={imgIndex === index ? 1 : 0}
                      justify="center"
                    >
                      <Tooltip label="设为参考图" placement="top">
                        <IconButton aria-label="structure img" size="xs" variant="unstyled" color="white" icon={<RiLogoutBoxRLine size={16} />} _hover={{ color: "#8E94A9" }} onClick={() => onStructure(item)} />
                      </Tooltip>
                      <Tooltip label="发布" placement="top">
                        <IconButton aria-label="publish img" size="xs" variant="unstyled" color="white" icon={<RiSendPlaneLine size={16} />} _hover={{ color: "#8E94A9" }} onClick={() => onPublish(item)} />
                      </Tooltip>
                      <Tooltip label="删除" placement="top">
                        <IconButton aria-label="delete img" size="xs" variant="unstyled" color="white" icon={<RiDeleteBin4Line size={16} />} _hover={{ color: "#8E94A9" }} onClick={() => onDelete(item)} />
                      </Tooltip>
                    </HStack>

                    <Image src={item.image_url} w="148px" h="150px" fit="cover" />
                  </Box>
                )}
              />
            </Box>
          </Box>
        )}
      </Flex>

      {currentContentId && <Publish isOpen={publishControl.isOpen} onClose={publishControl.onClose} contentId={currentContentId} />}

      {currentContentId && <TransitionDialog isOpen={dialogControl.isOpen} onClose={dialogControl.onClose} onDelete={handleOnDelete} />}

      <HistoryModal isOpen={historyControl.isOpen} onClose={historyControl.onClose} />
    </Box>
  );
};

export default GenImageHistory;
